import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | error
  const pollCount = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      if (pollCount.current >= 8) {
        setStatus("error");
        return;
      }
      pollCount.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refreshUser();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch {
        /* keep polling */
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [params, refreshUser]);

  return (
    <div className="min-h-screen bg-[#040914] bg-tech-grid flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-2xl neon-border bg-[#0B101A]/80 backdrop-blur-xl p-10 text-center"
      >
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-cyan-neon mx-auto animate-spin" />
            <h1 className="font-display text-2xl text-white mt-6">Confirming your payment…</h1>
            <p className="text-slate-400 mt-2 text-sm">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#39FF14] mx-auto" />
            <h1 className="font-display text-2xl text-white mt-6">You're protected!</h1>
            <p className="text-slate-400 mt-2 text-sm">Your subscription is active. The shield is now live on your account.</p>
            <button
              data-testid="payment-success-dashboard-btn"
              onClick={() => nav("/dashboard")}
              className="mt-8 w-full py-3 rounded-full bg-cyan-neon text-[#040914] font-semibold hover:glow-cyan transition-shadow"
            >
              Go to dashboard
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-14 h-14 text-red-400 mx-auto" />
            <h1 className="font-display text-2xl text-white mt-6">Couldn't confirm payment</h1>
            <p className="text-slate-400 mt-2 text-sm">If you were charged, your plan will activate shortly. Otherwise please try again.</p>
            <Link to="/dashboard" className="mt-8 inline-block w-full py-3 rounded-full border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors">
              Continue to dashboard
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-[#040914] bg-tech-grid flex items-center justify-center px-6">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0B101A]/80 backdrop-blur-xl p-10 text-center">
        <ShieldCheck className="w-12 h-12 text-slate-500 mx-auto" />
        <h1 className="font-display text-2xl text-white mt-6">Checkout cancelled</h1>
        <p className="text-slate-400 mt-2 text-sm">No charge was made. You can restart your trial anytime.</p>
        <Link to="/#pricing" className="mt-8 inline-block w-full py-3 rounded-full bg-cyan-neon text-[#040914] font-semibold">
          Back to pricing
        </Link>
      </div>
    </div>
  );
}
